import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SaleProductsPreview } from '@/components/import-flow/view-products-copy-previous-sales-preview';
import {
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { OptionReminder } from '@/components/ui/option-reminder';
import { useToast } from '@/hooks/use-toast';
import {
  type ToastUpdate,
  bindDealItemsInProgressToastToDatasource,
  clearDealItemsInProgressToast,
  registerDealItemsInProgressToast,
} from '@/lib/deal-items-toast';
import { DatasourceInstructionMode } from '@/lib/graphql/generated/graphql';
import { useAddPreviousCampaignToDeal } from '@/lib/hooks/useAddPreviousCampaignToDeal';
import { useErrorToastHandler } from '@/lib/hooks/useErrorToastHandler';
import { useLatestPreviousCampaigns } from '@/lib/hooks/useLatestPreviousCampaigns';
import { useSaleProductsPreview } from '@/lib/hooks/useSaleProductsPreview';
import { dealAtom } from '@/states/deal.state';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useAtomValue } from 'jotai';
import { ArrowLeft, Calendar, Package } from 'lucide-react';

import { BaseImportViewProps } from './import-view-props';

enum ModalStep {
  SELECTION = 'selection',
  CONFIRMATION = 'confirmation',
}

interface ViewProductsCopyPreviousSalesProps extends BaseImportViewProps {
  dealId: string;
  instructionMode?: DatasourceInstructionMode;
  onModifyOption?: () => void;
  onCloseDialog?: () => void;
  cameFromOptions?: boolean;
}

export const ViewProductsCopyPreviousSales: React.FC<
  ViewProductsCopyPreviousSalesProps
> = ({
  setImportView,
  dealId,
  instructionMode,
  onModifyOption,
  onCloseDialog,
  cameFromOptions = false,
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<ModalStep>(ModalStep.SELECTION);
  const [selectedSale, setSelectedSale] = useState<{
    saleId: string;
    name: string;
  } | null>(null);

  const deal = useAtomValue(dealAtom);
  const { previousCampaigns, loading, error } = useLatestPreviousCampaigns();
  const {
    addPreviousCampaignToDeal,
    loading: addingCampaign,
    error: previousCampaignError,
    domainError,
    isDomainError,
  } = useAddPreviousCampaignToDeal();
  const { toast } = useToast();
  const progressToastTrackerIdRef = useRef<string | null>(null);

  const clearProgressToast = () => {
    if (!progressToastTrackerIdRef.current) return;
    clearDealItemsInProgressToast({
      trackerId: progressToastTrackerIdRef.current,
    });
    progressToastTrackerIdRef.current = null;
  };

  const {
    products: previewProducts,
    loading: loadingPreview,
    error: previewError,
  } = useSaleProductsPreview(deal?.brandId || '', selectedSale?.saleId || '', {
    skip: !selectedSale || !deal?.brandId,
  });

  const handleSelectCampaign = (saleId: string, name: string) => {
    setSelectedSale({ saleId, name });
    setStep(ModalStep.CONFIRMATION);
  };

  const handleConfirmImport = async () => {
    if (!selectedSale) return;

    const { update, dismiss } = toast({
      description: `⏳ ${t('general.copy_previous_sales_in_progress')}`,
      duration: Number.POSITIVE_INFINITY,
    });
    const toastTrackerId = registerDealItemsInProgressToast(
      update as ToastUpdate,
      dismiss
    );
    progressToastTrackerIdRef.current = toastTrackerId;

    try {
      const mutationResult = await addPreviousCampaignToDeal(
        dealId,
        selectedSale.saleId,
        instructionMode
      );

      if (mutationResult?.__typename === 'MutationSuccess') {
        bindDealItemsInProgressToastToDatasource(
          toastTrackerId,
          mutationResult.id
        );
        progressToastTrackerIdRef.current = null;
        onCloseDialog?.();
      } else {
        clearProgressToast();
      }
    } catch (error) {
      clearProgressToast();
      console.error('Failed to import previous sale:', error);
    }
  };

  useErrorToastHandler({
    domainError,
    isDomainError,
    error: previousCampaignError ?? null,
    domainToast: {
      titleKey: 'general.error_import',
      defaultMessageKey: 'general.error_import_previous_sales_description',
      clearProgress: clearProgressToast,
    },
    genericToast: {
      titleKey: 'general.error_import',
      getDescription: () =>
        t('general.error_import_previous_sales_description'),
    },
  });

  const handleBack = () => {
    setStep(ModalStep.SELECTION);
    setSelectedSale(null);
  };

  const handleBackToProducts = () => {
    setImportView(
      cameFromOptions ? 'productsCopyPreviousSalesOptionSelection' : 'products'
    );
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'd MMMM yyyy', { locale: fr });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="flex h-[600px] flex-col">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <button
              className="rounded-md p-1 hover:bg-muted"
              onClick={handleBackToProducts}
              aria-label={t('components.import_flow.back_to_options')}
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              {t('components.import_flow.copy_previous_sales.title')}
            </DialogTitle>
          </div>
          <DialogDescription>
            {t('components.import_flow.copy_previous_sales.loading_sales')}
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-center py-8">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-[600px] flex-col">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <button
              className="rounded-md p-1 hover:bg-muted"
              onClick={handleBackToProducts}
              aria-label={t('components.import_flow.back_to_options')}
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              {t('components.import_flow.copy_previous_sales.title')}
            </DialogTitle>
          </div>
          <DialogDescription>
            {t('general.error_loading_previous_sales')}
          </DialogDescription>
        </DialogHeader>
        <div className="py-8 text-center text-sm text-gray-500">
          {t('general.error_network_message')}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-[600px] flex-col">
      <DialogHeader className="pb-4">
        <div className="flex items-center gap-2">
          <button
            className="rounded-md p-1 hover:bg-muted"
            onClick={handleBackToProducts}
            aria-label={t(
              'components.import_flow.copy_previous_sales.back_to_products'
            )}
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <DialogTitle className="flex items-center gap-2 text-lg font-semibold">
            <Package className="h-4 w-4" />
            {t('components.import_flow.copy_previous_sales.title')}
          </DialogTitle>
        </div>
        <DialogDescription className="mt-1 text-sm text-gray-600">
          {step === ModalStep.SELECTION
            ? t(
                'components.import_flow.copy_previous_sales.selection_description'
              )
            : t(
                'components.import_flow.copy_previous_sales.confirmation_description'
              )}
        </DialogDescription>
      </DialogHeader>

      {instructionMode && onModifyOption && step === ModalStep.SELECTION && (
        <div className="pb-6">
          <OptionReminder
            instructionMode={instructionMode}
            onModify={onModifyOption}
          />
        </div>
      )}

      {step === ModalStep.SELECTION ? (
        // Step 1: Campaign Selection
        addingCampaign ? (
          <div className="flex items-center justify-center py-8">
            <div className="flex flex-col items-center gap-2">
              <div className="h-6 w-6 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
              <p className="text-xs font-medium text-gray-700">
                {t('components.import_flow.copy_previous_sales.importing')}
              </p>
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            {previousCampaigns.length === 0 ? (
              <div className="py-8 text-center">
                <Package className="mx-auto mb-3 h-10 w-10 text-gray-400" />
                <p className="mx-auto max-w-md text-sm text-gray-500">
                  {t(
                    'components.import_flow.copy_previous_sales.no_sales_available'
                  )}{' '}
                  {t(
                    'components.import_flow.copy_previous_sales.no_sales_available_description'
                  )}
                </p>
              </div>
            ) : (
              <>
                {/* Header row */}
                <div className="mb-3 grid grid-cols-4 gap-4 border-b pb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
                  <div>
                    {t('components.import_flow.copy_previous_sales.sale_name')}
                  </div>
                  <div>
                    {t('components.import_flow.copy_previous_sales.date')}
                  </div>
                  <div>
                    {t('components.import_flow.copy_previous_sales.products')}
                  </div>
                  {/* <div>Valeur totale</div> */}
                </div>

                {/* Data rows */}
                <div className="space-y-0">
                  {previousCampaigns.map((campaign) => (
                    <div
                      key={campaign.saleId}
                      className="group grid cursor-pointer grid-cols-4 gap-4 border-b py-3 transition-colors last:border-b-0 hover:bg-gray-50"
                      onClick={() =>
                        handleSelectCampaign(campaign.saleId, campaign.name)
                      }
                    >
                      <div className="text-sm font-medium text-gray-900">
                        {campaign.name}
                      </div>

                      <div className="flex items-center gap-1.5 text-sm text-gray-600">
                        <Calendar className="h-3 w-3" />
                        {formatDate(campaign.startAt.toISOString())}
                      </div>

                      <div className="text-sm text-gray-600">
                        {t('general.product_count', {
                          count: campaign.numberOfProducts,
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )
      ) : (
        // Step 2: Product Preview & Confirmation
        <div className="flex-1 overflow-hidden">
          <SaleProductsPreview
            products={previewProducts}
            loading={loadingPreview}
            error={previewError}
            onConfirm={handleConfirmImport}
            onBack={handleBack}
            saleName={selectedSale?.name || ''}
            confirming={addingCampaign}
            instructionMode={instructionMode}
            onModifyOption={onModifyOption}
          />
        </div>
      )}
    </div>
  );
};
